import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { Button, Input, Modal, cx } from './ui';
import { errorMessage } from '../lib/api';
import { t } from '../lib/i18n';

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

type ToastTone = 'success' | 'error' | 'info' | 'warning';
interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}

interface ToastApi {
  success: (title: string, description?: string) => void;
  error: (title: string | unknown, description?: string) => void;
  info: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const remove = useCallback((id: number) => setItems((list) => list.filter((i) => i.id !== id)), []);
  const push = useCallback(
    (tone: ToastTone, title: string, description?: string) => {
      const id = next.current++;
      setItems((list) => [...list.slice(-4), { id, tone, title, description }]);
      setTimeout(() => remove(id), tone === 'error' ? 8000 : 4500);
    },
    [remove],
  );
  const api = useRef<ToastApi>({
    success: (title, d) => push('success', title, d),
    error: (title, d) => push('error', typeof title === 'string' ? title : errorMessage(title), d),
    info: (title, d) => push('info', title, d),
    warning: (title, d) => push('warning', title, d),
  });
  const icons = { success: CheckCircle2, error: XCircle, info: Info, warning: AlertTriangle };
  const colors = { success: 'text-emerald-600', error: 'text-red-600', info: 'text-blue-600', warning: 'text-amber-600' };
  return (
    <ToastContext.Provider value={api.current}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-full max-w-sm flex-col gap-2" aria-live="polite">
          {items.map((item) => {
            const Icon = icons[item.tone];
            return (
              <div key={item.id} className="pointer-events-auto flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-lg animate-slide-up">
                <Icon className={cx('mt-0.5 size-5 shrink-0', colors[item.tone])} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900">{item.title}</p>
                  {item.description && <p className="mt-0.5 text-sm text-slate-500">{item.description}</p>}
                </div>
                <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => remove(item.id)} aria-label={t('Dismiss')}>
                  <X className="size-4" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}

// ---------------------------------------------------------------------------
// Confirm / prompt dialogs (promise based)
// ---------------------------------------------------------------------------

interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** When set, the user must type a value (prompt). */
  input?: { label: string; placeholder?: string; defaultValue?: string; required?: boolean; multiline?: boolean };
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<string | false>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: string | false) => void }) | null>(null);
  const [value, setValue] = useState('');
  const confirm = useCallback<ConfirmFn>(
    (opts) =>
      new Promise((resolve) => {
        setValue(opts.input?.defaultValue ?? '');
        setState({ ...opts, resolve });
      }),
    [],
  );
  const close = (result: string | false) => {
    state?.resolve(result);
    setState(null);
  };
  const invalid = !!state?.input?.required && !value.trim();
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={!!state}
        onClose={() => close(false)}
        title={state?.title ?? ''}
        size="sm"
        footer={
          <>
            <Button onClick={() => close(false)}>{state?.cancelLabel ?? t('Cancel')}</Button>
            <Button variant={state?.danger ? 'danger' : 'primary'} disabled={invalid} onClick={() => close(state?.input ? value : 'ok')}>
              {state?.confirmLabel ?? t('Confirm')}
            </Button>
          </>
        }
      >
        {state?.description && <div className="text-sm text-slate-600">{state.description}</div>}
        {state?.input && (
          <div className="mt-3">
            <label className="label">{state.input.label}</label>
            {state.input.multiline ? (
              <textarea className="input min-h-24" value={value} placeholder={state.input.placeholder} onChange={(e) => setValue(e.target.value)} />
            ) : (
              <Input
                value={value}
                placeholder={state.input.placeholder}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !invalid && close(value)}
              />
            )}
          </div>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm outside ConfirmProvider');
  return ctx;
}
